import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, Wand2, Check } from "lucide-react";
import TopBar from "@/components/TopBar";
import { useLanguage } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATUS_STYLE = {
  new:       { label: "Nouveau",  cls: "text-[#A60F6E] bg-[#FCE7F3]" },
  contacted: { label: "Contacté", cls: "text-[#6B2C8F] bg-[#EFE3F5]" },
  done:      { label: "Terminé",  cls: "text-[#206F18] bg-[#E8F6E5]" },
};

export default function AdminCustomRequests() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/custom-gift-requests");
    setRows(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/custom-gift-requests/${id}`, { status });
    load();
  };

  return (
    <div className="min-h-screen bg-[#FFF9FB]">
      <TopBar />
      <div className="max-w-5xl mx-auto px-5 pb-24">
        <Button
          variant="ghost"
          onClick={() => navigate("/admin")}
          data-testid="custom-requests-back"
          className="gap-2 -ml-3 mt-2 text-[#5C4664] hover:bg-[#FCE7F3]"
        >
          <ArrowLeft className="w-4 h-4" /> {t("admin.backToDashboard")}
        </Button>

        <div className="mt-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#EFE3F5] text-[#6B2C8F] flex items-center justify-center">
            <Wand2 className="w-5 h-5" />
          </div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C]">
              {t("admin.customRequests")}
            </h1>
            <p className="mt-1 text-sm text-[#5C4664]">{rows.length} demande(s)</p>
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="py-16 text-center text-[#5C4664]">{t("common.loading")}</div>
          ) : rows.length === 0 ? (
            <div data-testid="custom-empty" className="py-16 text-center text-[#5C4664]">
              Aucune demande pour l&apos;instant.
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map((r) => {
                const style = STATUS_STYLE[r.status] || STATUS_STYLE.new;
                return (
                  <div key={r.id} data-testid={`custom-row-${r.id}`} className="bg-white rounded-2xl border border-[#F2E2EC] p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="font-display text-lg font-bold text-[#2A0E3C]">
                          {r.first_name} {r.last_name}
                        </div>
                        <a href={`tel:${r.phone}`} className="mt-1 inline-flex items-center gap-1.5 text-sm text-[#D81B8C]">
                          <Phone className="w-3.5 h-3.5" /> {r.phone}
                        </a>
                        {r.event_code && (
                          <div className="mt-1 text-xs text-[#5C4664]">
                            Événement : <span className="font-mono font-bold text-[#2A0E3C]">{r.event_code}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${style.cls}`}>
                          {style.label}
                        </span>
                        <span className="text-xs text-[#5C4664]">
                          {new Date(r.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-[#2A0E3C] bg-[#FFF9FB] p-3 rounded-xl border border-[#F2E2EC]">
                      « {r.wish} »
                    </p>
                    <div className="mt-3 flex gap-2 flex-wrap">
                      {r.status !== "contacted" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setStatus(r.id, "contacted")}
                          data-testid={`status-contacted-${r.id}`}
                          className="rounded-full border-[#F2E2EC] text-xs"
                        >
                          Marquer contacté
                        </Button>
                      )}
                      {r.status !== "done" && (
                        <Button
                          size="sm"
                          onClick={() => setStatus(r.id, "done")}
                          data-testid={`status-done-${r.id}`}
                          className="rounded-full bg-[#3FA535] hover:bg-[#2E7E27] text-xs gap-1.5"
                        >
                          <Check className="w-3 h-3" /> Terminé
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
